import { useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import { AxiosError } from 'axios'
import GenericError from 'components/GenericError'
import { stepNameToPathSegment } from 'config/flow'
import usePage from 'hooks/usePage'
import { useAnswers } from 'providers/AnswersProvider'
import { useFlowEngine } from 'providers/FlowEngineProvider'
import { usePatient } from 'providers/PatientProvider'

import { AnamnesisStep } from '../@types/Patient'
import ServerError from '../@types/ServerError'
import SignDocumentScreen from '../screens/SignDocument'
import api from '../services/api'
import { getSignedDocumentDownloadUrl } from '../services/api/signedDocuments'

type DocumentPageParams = {
  stepName: string
}

const findStepByPathSegment = (segment: string, flow: { step: AnamnesisStep }[]): AnamnesisStep | undefined =>
  flow.find(({ step }) => stepNameToPathSegment(step.step) === segment)?.step

const DocumentPage: React.FC = () => {
  const { stepName: pathSegment } = useParams<DocumentPageParams>()
  const page = usePage()
  const flowEngine = useFlowEngine()
  const answers = useAnswers()
  const patient = usePatient()
  const { i18n } = useTranslation()

  const step = useMemo(() => findStepByPathSegment(pathSegment, flowEngine.flow), [pathSegment, flowEngine.flow])

  const [pdfLink, setPdfLink] = useState<string | undefined>()

  const params = useMemo(
    () => ({
      patientReferenceId: patient?.data?.referenceId || '',
      step: step?.step ?? '',
      language: i18n.language?.toUpperCase() || 'DE',
      code: answers.code || '',
    }),
    [patient?.data?.referenceId, step?.step, i18n.language, answers.code]
  )

  const queryEnabled = !!step && !!params.patientReferenceId && !!params.code

  useQuery(['signed-document', params], () => api.signedDocuments.getSignedDocument(params), {
    retry: (count: number, error: AxiosError) => error.response?.status !== 409 && count <= 3,
    enabled: queryEnabled,
    onSuccess: () => {
      setPdfLink(getSignedDocumentDownloadUrl(params))
    },
    onError: (error: AxiosError<ServerError>) => {
      if (error.response?.status === 404 || error.response?.status === 409) {
        // Skip the document if it could not be found (404) or was already signed (409)
        page.onSubmit({})
      }
    },
  })

  const { mutateAsync: signDocument, ...signDocumentStatus } = useMutation(api.signedDocuments.signSignedDocument, {
    onSuccess: () => {
      page.onSubmit({})
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        page.onSubmit({})
      }
    },
  })

  const handleSignatureSubmit = useCallback(
    (signature: string) => {
      if (!step || !params.patientReferenceId) {
        throw new Error('Cannot sign document without an active step and patient.')
      }

      signDocument({
        ...params,
        signatureEncoded: signature.replace('data:image/png;base64,', ''),
      })
    },
    [params, signDocument, step]
  )

  if (!step) {
    return <GenericError title="generic_error_title" description="document_not_found_description" />
  }

  return (
    <SignDocumentScreen
      {...page}
      onSignatureSubmit={handleSignatureSubmit}
      pdfLink={pdfLink}
      signatureLoading={signDocumentStatus.isLoading}
      titleKey={`screen.document.${pathSegment}.title`}
      subtitleKey={`screen.document.${pathSegment}.subtitle`}
      descriptionKey={`screen.document.${pathSegment}.description`}
    />
  )
}

export default DocumentPage
